// Generated by TaroBase SDK Generator: 2026-04-14T11:42:05.668Z
// Collection: luckyDrawEntries/$entryId
// Edits may be overwritten by subsequent runs!

import {
  get, getMany, set, subscribe, getFiles, setFile, runQuery, count, aggregate, transformValues,
  Time, Token, Increment, Address, PublicKey,
  TimeOperation, IncrementOperation, TokenAmount, AddressType,
  TarobaseMetadata, DocumentOperation, FileItem, setMany, formatError,
} from '../db-client';
import type { AggregateResult, AggregateOperation } from '../db-client';

/**
 * Lucky draw entries for Danger Dan! Users enter via verified Twitter handle (no wallet required). Winners can later submit their Solana wallet address to claim prizes.
 */

export interface LuckyDrawEntriesRequest {
  walletAddress?: string;
  twitterHandle?: string;
  prizeType: string;
  won: boolean;
  drawnAt: number | TimeOperation | IncrementOperation | TokenAmount;
  claimedWalletAddress?: string;
  adminStatus?: string;
  adminRemark?: string;
}

export interface LuckyDrawEntriesResponse {
  walletAddress?: string;
  twitterHandle?: string;
  prizeType: string;
  won: boolean;
  drawnAt: number;
  claimedWalletAddress?: string;
  adminStatus?: string;
  adminRemark?: string;
  id: string;
  tarobase_created_at: number;
}

/**
 * Build a LuckyDrawEntries operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildLuckyDrawEntries(entryId: string, data?: LuckyDrawEntriesRequest): DocumentOperation {
  const doc = data ? transformValues(data) : {};
  return { path: `luckyDrawEntries/${entryId}`, document: doc };
}

/**
 * Anyone can create a draw entry without wallet connection. Twitter identity is verified by the backend before allowing the draw. twitterHandle is the primary identifier and is immutable. walletAddress is optional (set if user has a connected wallet). prizeType, won, and drawnAt are immutable after creation. (Create/Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the write worked.
 */
export async function setLuckyDrawEntries(entryId: string, data?: LuckyDrawEntriesRequest): Promise<boolean> {
  try {
    const transformedData = transformValues(data || {});
    const succeeded = await set(`luckyDrawEntries/${entryId}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error setting LuckyDrawEntries: ${formatError(error)}`);
    return false;
  }
}

export type LuckyDrawEntriesRequestUpdate = Partial<LuckyDrawEntriesRequest>;

/**
 * Build a LuckyDrawEntries update operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildUpdateLuckyDrawEntries(entryId: string, data: LuckyDrawEntriesRequestUpdate): DocumentOperation {
  const doc = transformValues(data);
  return { path: `luckyDrawEntries/${entryId}`, document: doc };
}

/**
 * Admin, founder, or backend (PROJECT_VAULT_ADDRESS) only. Used to save the winner's claimedWalletAddress after win verification, and to set adminStatus (e.g. 'pending_transfer', 'nft_transferred', 'voucher_recorded', 'voucher_used', 'contacted', 'no_response', 'cancelled') and adminRemark (free-text note) for prize fulfilment tracking. (Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the update worked.
 */
export async function updateLuckyDrawEntries(entryId: string, data: LuckyDrawEntriesRequestUpdate): Promise<boolean> {
  try {
    const transformedData = transformValues(data);
    const succeeded = await set(`luckyDrawEntries/${entryId}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error updating LuckyDrawEntries: ${formatError(error)}`);
    return false;
  }
}

/**
 * 
  Read Operation Details: Only the entry owner (matching walletAddress if provided), admin, or founder can read a draw entry. Includes adminStatus and adminRemark fields visible to admin/founder for prize fulfilment tracking.
   (Get Single Item)
 */
export async function getLuckyDrawEntries(entryId: string): Promise<LuckyDrawEntriesResponse | null> {
  try {
    const result = await get(`luckyDrawEntries/${entryId}`);
    return result as LuckyDrawEntriesResponse | null;
  } catch (error) {
    console.error(`Error getting LuckyDrawEntries: ${formatError(error)}`);
    return null;
  }
}

/**
 * Subscribes to changes in a single LuckyDrawEntries document. (
  Read Operation Details: Only the entry owner (matching walletAddress if provided), admin, or founder can read a draw entry. Includes adminStatus and adminRemark fields visible to admin/founder for prize fulfilment tracking.
  )
 */
export function subscribeLuckyDrawEntries(callback: (data: LuckyDrawEntriesResponse | null) => void, entryId: string): Promise<() => Promise<void>> {
  return subscribe(`luckyDrawEntries/${entryId}`, { // Use the item's basePath
    onData: (data) => {
      callback(data as LuckyDrawEntriesResponse | null);
    },
    onError: (error) => {
      console.error(`Error subscribing to LuckyDrawEntries at path '${`luckyDrawEntries/${entryId}`}': ${formatError(error)}`);
    }
  });
}

/**
 * Get many LuckyDrawEntries items from collection luckyDrawEntries
 
  Read Operation Details: Only the entry owner (matching walletAddress if provided), admin, or founder can read a draw entry. Includes adminStatus and adminRemark fields visible to admin/founder for prize fulfilment tracking.
  
 */
export async function getManyLuckyDrawEntries(filter: string = ""): Promise<LuckyDrawEntriesResponse[]> {
  try {
    const results = await get("luckyDrawEntries", { prompt: filter });
    return Array.isArray(results) ? results as LuckyDrawEntriesResponse[] : [];
  } catch (error) {
    console.error(`Error getting LuckyDrawEntries collection at path 'luckyDrawEntries': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in LuckyDrawEntries collection at luckyDrawEntries
 
  Read Operation Details: Only the entry owner (matching walletAddress if provided), admin, or founder can read a draw entry. Includes adminStatus and adminRemark fields visible to admin/founder for prize fulfilment tracking.
  
 */
export function subscribeManyLuckyDrawEntries(callback: (data: LuckyDrawEntriesResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("luckyDrawEntries", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as LuckyDrawEntriesResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to LuckyDrawEntries collection at path '${"luckyDrawEntries"}': ${formatError(error)}`);
    }
  });
}

/**
 * Get all LuckyDrawEntries items from collection luckyDrawEntries
 
  Read Operation Details: Only the entry owner (matching walletAddress if provided), admin, or founder can read a draw entry. Includes adminStatus and adminRemark fields visible to admin/founder for prize fulfilment tracking.
  
 */
export async function getAllLuckyDrawEntries(filter: string = ""): Promise<LuckyDrawEntriesResponse[]> {
  try {
    const results = await get("luckyDrawEntries", { prompt: filter });
    return Array.isArray(results) ? results as LuckyDrawEntriesResponse[] : [];
  } catch (error) {
    console.error(`Error getting LuckyDrawEntries collection at path 'luckyDrawEntries': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in LuckyDrawEntries collection at luckyDrawEntries
 
  Read Operation Details: Only the entry owner (matching walletAddress if provided), admin, or founder can read a draw entry. Includes adminStatus and adminRemark fields visible to admin/founder for prize fulfilment tracking.
  
 */
export function subscribeAllLuckyDrawEntries(callback: (data: LuckyDrawEntriesResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("luckyDrawEntries", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as LuckyDrawEntriesResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to LuckyDrawEntries collection at path '${"luckyDrawEntries"}': ${formatError(error)}`);
    }
  });
}

/**
 * 
  Delete Operation Details: Admin or founder only. Can remove draw entries for moderation or cleanup.
   (Delete Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the delete worked.
 */
export async function deleteLuckyDrawEntries(entryId: string): Promise<boolean> {
  try {
    const succeeded = await set(`luckyDrawEntries/${entryId}`, null);
    return !!succeeded;
  } catch (error) {
    console.error(`Error deleting LuckyDrawEntries: ${formatError(error)}`);
    return false;
  }
}

/**
 * Build a delete operation for LuckyDrawEntries for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildDeleteLuckyDrawEntries(entryId: string): DocumentOperation {
  return { path: `luckyDrawEntries/${entryId}`, document: null };
}

